import { useState, useEffect, useRef } from "react";
import { formatErr } from "../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { createInquiry } from "../lib/firestoreInquiries";

const USE_CASES = [
  { key: "onboarding", label: "The Onboarding Cap" },
  { key: "event", label: "Conference / event" },
  { key: "merchandise", label: "Brand merchandise" },
  { key: "other", label: "Something else" },
];

const CAROUSEL_ITEMS = [
  { src: '/assets/images/crousel_1.jpg', caption: 'Hand-finished sample' },
  { src: '/assets/images/crousel_2.jpg', caption: 'Signature stitches' },
  { src: '/assets/images/crousel_3.jpeg', caption: 'Luxury materials' },
  { src: '/assets/images/crousel_4.jpeg', caption: 'Finished drop' },
  { src: '/assets/images/crousel_5.jpg', caption: 'Hand-finished sample' },
  { src: '/assets/images/crousel_6.jpg', caption: 'Signature stitches' },
  { src: '/assets/images/crousel_7.jpg', caption: 'Luxury materials' },
  { src: '/assets/images/crousel_8.jpeg', caption: 'Finished drop' },
];

function Carousel({ items = CAROUSEL_ITEMS }) {
  const [index, setIndex] = useState(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // autoplay
    timeoutRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 3500);
    return () => clearInterval(timeoutRef.current);
  }, [items.length]);

  const prevIndex = (index - 1 + items.length) % items.length;
  const nextIndex = (index + 1) % items.length;

  const goPrev = () => setIndex((i) => (i - 1 + items.length) % items.length);
  const goNext = () => setIndex((i) => (i + 1) % items.length);

  return (
    <div className="bg-black text-white py-8">
      <div className="max-w-6xl mx-auto px-6">
        <div className="relative rounded-lg overflow-hidden" style={{ height: 420 }}>
          {/* center stage layout: left preview, center, right preview */}
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Left preview */}
            <motion.img
              key={items[prevIndex].src + "-left"}
              src={items[prevIndex].src}
              alt={items[prevIndex].caption}
              initial={{ opacity: 0, x: -80, scale: 0.92 }}
              animate={{ opacity: 0.65, x: -40, scale: 0.94 }}
              transition={{ duration: 0.6 }}
              className="object-cover rounded shadow-lg"
              style={{ width: '28%', height: '78%', transformOrigin: 'center', zIndex: 10 }}
            />

            {/* Center image */}
            <motion.img
              key={items[index].src + "-center"}
              src={items[index].src}
              alt={items[index].caption}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="object-cover rounded shadow-2xl"
              style={{ width: '44%', height: '86%', zIndex: 20 }}
            />

            {/* Right preview */}
            <motion.img
              key={items[nextIndex].src + "-right"}
              src={items[nextIndex].src}
              alt={items[nextIndex].caption}
              initial={{ opacity: 0, x: 80, scale: 0.92 }}
              animate={{ opacity: 0.65, x: 40, scale: 0.94 }}
              transition={{ duration: 0.6 }}
              className="object-cover rounded shadow-lg"
              style={{ width: '28%', height: '78%', transformOrigin: 'center', zIndex: 10 }}
            />

            {/* Caption */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#C9A961] bg-black/50 px-4 py-2 rounded text-base">
              {items[index].caption}
            </div>

            {/* Arrows */}
            <button
              aria-label="Previous"
              onClick={goPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 p-3 rounded-full"
              style={{ zIndex: 30 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>

            <button
              aria-label="Next"
              onClick={goNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 p-3 rounded-full"
              style={{ zIndex: 30 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

        </div>

        {/* Thumbnails - single non-wrapping row */}
        <div className="mt-6">
          <div className="flex gap-3 overflow-x-auto items-center" style={{ WebkitOverflowScrolling: 'touch', whiteSpace: 'nowrap' }}>
            {items.map((it, i) => (
              <button
                key={it.src + i}
                type="button"
                onClick={() => setIndex(i)}
                className={`flex-shrink-0 rounded overflow-hidden ${i === index ? 'ring-2 ring-[#C9A961]' : ''}`}
                style={{ width: 120, height: 72 }}
              >
                <img
                  src={it.src}
                  alt={it.caption}
                  className={`w-full h-full object-cover ${i === index ? '' : 'opacity-60'}`}
                />
                <div className="text-xs text-white/70 text-center mt-1 hidden">{it.caption}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CustomPage() {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    quantity: 50,
    use_case: "onboarding",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createInquiry({
        source: "custom_bulk",
        ...form,
        quantity: Number(form.quantity),
      });
      setDone(true);
      toast.success("Inquiry received");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="pt-32 px-6 md:px-12 min-h-screen" data-testid="inquiry-success">
        <div className="max-w-2xl">
          <CheckCircle2 className="text-[#C9A961] mb-6" size={40} strokeWidth={1.2} />
          <div className="label-eyebrow mb-3">Inquiry received</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            Thank you, {form.contact_name.split(" ")[0]}.
          </h1>
          <p className="mt-6 text-black/60 max-w-md">
            One of the atelier team will respond within 24 hours with a tailored quote and lead-time.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="custom-page">
      <div className="grid md:grid-cols-12">
        <div className="md:col-span-5 bg-[#0A0A0A] text-white p-8 md:p-16 lg:p-20 flex flex-col justify-between min-h-[60vh] md:min-h-screen">
          <div>
            <div className="label-eyebrow text-white/50 mb-8">Custom &amp; Bulk Atelier</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.92]">
              Your brand,<br />
              <span className="text-[#C9A961] italic font-light">at 24 stitches</span><br />
              per emblem.
            </h1>
            <p className="mt-8 text-white/70 leading-relaxed max-w-sm">
              Embroidered Onboarding Cap, founder gifts, conference drops, and limited brand collaborations.
              Minimum order 50 units.
            </p>
          </div>
          <div className="mt-16 space-y-6">
            {[
              { n: "01", t: "Submit brief", d: "Share quantity, use case, and brand assets." },
              { n: "02", t: "Receive sample", d: "Physical sample + quote within 5 working days." },
              { n: "03", t: "Production", d: "Hand-finished in batches of 50. From 14 days." },
            ].map((s) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex gap-4"
              >
                <span className="label-eyebrow text-[#C9A961] mt-1">{s.n}</span>
                <div>
                  <div className="font-display text-lg">{s.t}</div>
                  <div className="text-sm text-white/50">{s.d}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-7 p-8 md:p-16 lg:p-20" data-testid="inquiry-form">
          <div className="label-eyebrow mb-3">Brief</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-12">Tell us what you need.</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Company" value={form.company} onChange={update("company")} required testId="inq-company" />
            <Field label="Your name" value={form.contact_name} onChange={update("contact_name")} required testId="inq-name" />
            <Field label="Email" type="email" value={form.email} onChange={update("email")} required testId="inq-email" />
            <Field label="Phone" value={form.phone} onChange={update("phone")} testId="inq-phone" />
            <Field label="Quantity (min 50)" type="number" min="50" value={form.quantity} onChange={update("quantity")} required testId="inq-quantity" />
            <Field label="Budget (optional)" value={form.budget} onChange={update("budget")} placeholder="e.g. ₹5,000–₹10,000" testId="inq-budget" />
          </div>

          <div className="mt-8">
            <label className="label-eyebrow block mb-3">Use case</label>
            <div className="flex flex-wrap gap-2">
              {USE_CASES.map((u) => (
                <button
                  key={u.key}
                  type="button"
                  data-testid={`use-case-${u.key}`}
                  onClick={() => setForm({ ...form, use_case: u.key })}
                  className={`px-4 py-2 text-sm border transition-colors ${
                    form.use_case === u.key
                      ? "bg-black text-white border-black"
                      : "border-black/30 hover:border-black"
                  }`}
                >
                  {u.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <label className="label-eyebrow block mb-3">Tell us more...</label>
            <textarea
              rows={5}
              required
              data-testid="inq-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Timing, logo, special requests…"
              className="w-full border border-black/30 px-4 py-3 focus:border-black focus:outline-none bg-white"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="inq-submit"
            className="mt-10 inline-flex items-center gap-2 bg-black text-white px-8 py-4 text-sm tracking-wide hover:bg-[#C9A961] transition-colors disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Submit brief"} <ArrowRight size={14} />
          </button>
        </form>
      </div>

      {/* Carousel appears below the grid only on the Custom page */}
      <Carousel />

    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <input
        {...props}
        data-testid={testId}
        className="w-full border border-black/30 px-4 py-3 focus:border-black focus:outline-none bg-white"
      />
    </label>
  );
}
